import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layout/MainLayout';
import Login from './pages/login/login/Login';
import Dashboard from './pages/dashboard/dashboard/Dashboard';
import Orders from './pages/order/orders/Orders';
import ActionRequired from './pages/order/action-required/ActionRequired';
import OrderEdit from './pages/order/edit/OrderEdit';
import OrderView from './pages/order/view/OrderView';
import CreateOrder from './pages/order/create/CreateOrder';
import BulkOrdersList from './pages/bulk-order/list/BulkOrdersList';
import CreateBulkOrder from './pages/bulk-order/create/CreateBulkOrder';
import BulkOrderView from './pages/bulk-order/view/BulkOrderView';
import BulkOrderEdit from './pages/bulk-order/edit/BulkOrderEdit';
import UserList from './pages/user/list/UserList';
import TaskList from './pages/task/list/TaskList';
import TaskCompletionsList from './pages/task/completions/TaskCompletionsList';
import MyWorkPage from './pages/task/my-work/MyWorkPage';
import EtsyAccounting from './pages/etsy-accounting/EtsyAccounting';
import ErrorLogs from './pages/errors/ErrorLogs';
import RepairsList from './pages/repair/list/RepairsList';
import { RepairsNewRedirect, RepairsEditRedirect, RepairsViewRedirect } from './pages/repair/RepairRouteRedirects';
import EncyclopediaList from './pages/encyclopedia/list/EncyclopediaList';
import CreateEncyclopedia from './pages/encyclopedia/create/CreateEncyclopedia';
import EncyclopediaView from './pages/encyclopedia/view/EncyclopediaView';
import EncyclopediaEdit from './pages/encyclopedia/edit/EncyclopediaEdit';
import Loading from './components/loading/Loading';
import SplashScreen from './components/splash/SplashScreen';
import { message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { removeMessage, setMessage } from "./redux/message.reducer";
import { removeAuth, setUserDetail } from "./redux/auth.reducer";
import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { PWAProvider, usePWA } from "./pwa/PWAContext";
import './styles/buttons.css';
import './App.css';
import { requestNotificationPermission, onMessageListener } from "./firebase";
import { isAdmin, isDeveloper, canOrderCreate, canOrderEdit, canAccessEtsyAccounting } from './utils/userRoles';

function AppContent() {
  const { message: payloadMessage, type } = useSelector((state) => state.messageReducer)
  const { isLoggedIn, user } = useSelector((state) => state.authReducer)
  const isAdminUser = isAdmin(user)
  const canCreateOrder = canOrderCreate(user)
  const canEditOrder = canOrderEdit(user)
  const canAccessAccounting = canAccessEtsyAccounting(user)
  const isDeveloperUser = isDeveloper(user)
  const { showForegroundNotification, notificationPermission } = usePWA()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [splashVisible, setSplashVisible] = useState(true)
  const prevUnreadRef = useRef(null)
  const fcmUnsubRef = useRef(null)
  const userDetailsFetchedRef = useRef(false)

  const callUserDetailsApi = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true)
    }
    try {
      const response = await axios.get('/users/me')
      if (response.data?.user) {
        const newUnread = response.data.user.unreadNotifications ?? 0
        prevUnreadRef.current = newUnread

        dispatch(setUserDetail({
          email: response.data?.user?.email,
          name: response.data?.user?.name,
          roles: response.data?.user?.roles || [],
          notificationsList: response.data?.user?.notificationsList || [],
          unreadNotifications: newUnread,
        }))
      }
    } catch (error) {
      dispatch(setMessage({ message: error.response?.data?.error, type: 'error' }))
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      if (!userDetailsFetchedRef.current) {
        userDetailsFetchedRef.current = true
        callUserDetailsApi(true)
      }
      requestNotificationPermission();
      const interval = setInterval(() => {
        callUserDetailsApi(false)
      }, 5000)
      return () => {
        clearInterval(interval)
      }
    } else {
      userDetailsFetchedRef.current = false
      prevUnreadRef.current = null
      setLoading(false)
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (!isLoggedIn || notificationPermission !== "granted") return
    const promise = onMessageListener((payload) => {
      const title = payload?.notification?.title || payload?.data?.title || "New notification"
      const body = payload?.notification?.body || payload?.data?.body || ""
      showForegroundNotification(title, body)
    })
    promise.then((unsub) => {
      fcmUnsubRef.current = unsub
    })
    return () => {
      if (typeof fcmUnsubRef.current === "function") {
        fcmUnsubRef.current()
        fcmUnsubRef.current = null
      }
    }
  }, [isLoggedIn, notificationPermission, showForegroundNotification])

  useEffect(() => {
    if (type && payloadMessage) {
      if (type == "error") {
        message.error(payloadMessage);
      } else if (type == "success") {
        message.success(payloadMessage);
      }
    }
    setTimeout(() => {
      dispatch(removeMessage())
    }, 300)
  }, [payloadMessage])

  if (splashVisible) {
    return <SplashScreen onDone={() => setSplashVisible(false)} />;
  }

  return loading ? (
    <Loading />
  ) : (
    <BrowserRouter>
      <PWAInstallBanner />
      <div className="App">
        <Routes>
          {isLoggedIn ? (
            <Route element={<MainLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/orders/new" element={canCreateOrder ? <CreateOrder /> : <Navigate to="/orders" replace />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/action-required" element={<ActionRequired />} />
              <Route path="/orders/:id" element={<OrderView />} />
              <Route path="/orders/:id/edit" element={canEditOrder ? <OrderEdit /> : <Navigate to="/orders" replace />} />
              <Route path="/bulk-orders" element={<BulkOrdersList />} />
              <Route path="/bulk-orders/new" element={canCreateOrder ? <CreateBulkOrder /> : <Navigate to="/bulk-orders" replace />} />
              <Route path="/bulk-orders/:id" element={<BulkOrderView />} />
              <Route path="/bulk-orders/:id/edit" element={canEditOrder ? <BulkOrderEdit /> : <Navigate to="/bulk-orders" replace />} />
              <Route path="/users" element={isAdminUser ? <UserList /> : <Navigate to="/dashboard" replace />} />
              <Route path="/tasks" element={isAdminUser ? <TaskList /> : <Navigate to="/dashboard" replace />} />
              <Route path="/tasks/create" element={isAdminUser ? <Navigate to="/tasks" state={{ openCreate: true }} replace /> : <Navigate to="/dashboard" replace />} />
              <Route path="/task-completions" element={isAdminUser ? <TaskCompletionsList /> : <Navigate to="/dashboard" replace />} />
              <Route path="/repairs" element={<RepairsList />} />
              <Route path="/repairs/new" element={<RepairsNewRedirect />} />
              <Route path="/repairs/:id/edit" element={<RepairsEditRedirect />} />
              <Route path="/repairs/:id" element={<RepairsViewRedirect />} />
              <Route path="/encyclopedia" element={<EncyclopediaList />} />
              <Route path="/encyclopedia/new" element={isAdminUser ? <CreateEncyclopedia /> : <Navigate to="/encyclopedia" replace />} />
              <Route path="/encyclopedia/:id" element={<EncyclopediaView />} />
              <Route path="/encyclopedia/:id/edit" element={isAdminUser ? <EncyclopediaEdit /> : <Navigate to="/encyclopedia" replace />} />
              <Route path="/my-tasks" element={<MyWorkPage />} />
              <Route path="/etsy-accounting" element={canAccessAccounting ? <EtsyAccounting /> : <Navigate to="/dashboard" replace />} />
              <Route path="/errors" element={isDeveloperUser ? <ErrorLogs /> : <Navigate to="/dashboard" replace />} />
              <Route path="/my-completions" element={<Navigate to="/my-tasks?tab=completions" replace />} />
              <Route path="/my-rewards" element={<Navigate to="/my-tasks?tab=rewards" replace />} />
            </Route>
          ) : (
            <Route path="/login" element={<Login />} />
          )}
          <Route path="*" element={<Navigate to={isLoggedIn ? "/dashboard" : "/login" } replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

function PWAInstallBanner() {
  const { canInstall, isInstalled, install } = usePWA();
  const [dismissed, setDismissed] = useState(false);
  if (!canInstall || isInstalled || dismissed) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: '#000', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
    }}>
      <span style={{ fontSize: 14 }}>Install the app for a better experience</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setDismissed(true)} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #666', color: '#fff', borderRadius: 4, cursor: 'pointer' }}>Not now</button>
        <button type="button" onClick={() => install().then(() => setDismissed(true))} style={{ padding: '6px 12px', background: '#1890ff', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer' }}>Install</button>
      </div>
    </div>
  );
}

function App() {
  return (
    <PWAProvider>
      <AppContent />
    </PWAProvider>
  );
}

export default App;
